class TestimonialsSlider extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector('.testimonials-slider__track');
    this.slides = Array.from(this.querySelectorAll('.testimonial-card'));
    this.prevButton = this.querySelector('.testimonials-slider__arrow--prev');
    this.nextButton = this.querySelector('.testimonials-slider__arrow--next');

    this.currentIndex = 0;
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplayDelay = parseInt(this.dataset.autoplayDelay) || 5000;
    this.originalSlidesPerView = parseInt(this.dataset.slidesPerView) || 1;
    this.slidesPerView = this.originalSlidesPerView;
    this.autoplayTimer = null;

    this.init();
  }

  init() {
    if (!this.track || this.slides.length === 0) return;

    // Set up track
    this.setupTrack();

    // Add event listeners
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }

    // Start autoplay
    if (this.autoplayEnabled) {
      this.startAutoplay();

      // Pause on hover
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
    }

    // Handle window resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.updateResponsiveSlidesPerView();
        this.updateSlidePosition();
      }, 250);
    });

    // Initial setup
    this.updateResponsiveSlidesPerView();
    this.updateSlidePosition();
  }

  setupTrack() {
    this.track.style.display = 'flex';
    this.track.style.transition = 'transform 0.5s ease-in-out';
  }

  getMaxIndex() {
    return Math.max(0, this.slides.length - this.slidesPerView);
  }

  updateResponsiveSlidesPerView() {
    const isMobile = window.innerWidth < 750;
    const previousSlidesPerView = this.slidesPerView;

    // Force 1 slide on mobile, use original setting on desktop
    this.slidesPerView = isMobile ? 1 : this.originalSlidesPerView;

    // Reset index if it's out of bounds after changing slides per view
    if (previousSlidesPerView !== this.slidesPerView) {
      const maxIndex = this.getMaxIndex();
      if (this.currentIndex > maxIndex) {
        this.currentIndex = maxIndex;
      }
    }
  }

  updateSlidePosition() {
    const containerWidth = this.querySelector('.testimonials-slider__wrapper')?.offsetWidth || 0;

    // Get the actual gap from computed styles
    const trackStyles = window.getComputedStyle(this.track);
    const gap = parseFloat(trackStyles.gap) || 0;

    // Calculate the actual slide width including gap
    const actualSlideWidth = (containerWidth - (gap * (this.slidesPerView - 1))) / this.slidesPerView;

    // Set each slide's flex-basis based on slides per view
    this.slides.forEach(slide => {
      slide.style.flexBasis = `${actualSlideWidth}px`;
      slide.style.minWidth = `${actualSlideWidth}px`;
    });

    // Calculate offset: move by (slide width + gap) for each index position
    // This accounts for the gap between slides in the flex container
    const moveDistance = actualSlideWidth + gap;
    const offset = -(this.currentIndex * moveDistance);

    this.track.style.transform = `translateX(${offset}px)`;

    // Update arrow states
    this.updateArrowStates();
  }

  updateArrowStates() {
    // Disable arrows at boundaries
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
    }

    if (this.nextButton) {
      const maxIndex = this.getMaxIndex();
      this.nextButton.disabled = this.currentIndex >= maxIndex;
    }
  }

  next() {
    const maxIndex = this.getMaxIndex();
    // Move by 1 slide at a time
    const newIndex = Math.min(this.currentIndex + 1, maxIndex);

    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex;
      this.updateSlidePosition();
      this.resetAutoplay();
    }
  }

  prev() {
    // Move by 1 slide at a time
    const newIndex = Math.max(this.currentIndex - 1, 0);

    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex;
      this.updateSlidePosition();
      this.resetAutoplay();
    }
  }

  startAutoplay() {
    if (!this.autoplayEnabled) return;

    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      const maxIndex = this.getMaxIndex();
      if (this.currentIndex >= maxIndex) {
        // Stop autoplay when reaching the end
        this.stopAutoplay();
      } else {
        this.currentIndex++;
        this.updateSlidePosition();
      }
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    if (this.autoplayEnabled) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }
}

customElements.define('testimonials-slider', TestimonialsSlider);
